import { OracleProvider } from "@bridgesplit/abf-sdk";
import {
    TextVariant,
    Row,
    AddressText,
    Text,
    Image,
    FONT_SIZES,
    TagText,
    Column,
    Tooltip,
    S3_PATH
} from "@bridgesplit/ui";
import { formatUsd } from "@bridgesplit/utils";
import { Divider } from "@mui/material";

type OracleMeta = { iconName: string; name: string };

// Unknown is excluded because it has no honest label, and callers already
// handle missing meta.
const ORACLE_META: Record<Exclude<OracleProvider, "unknown">, OracleMeta> = {
    pyth_legacy: { name: "Pyth Legacy", iconName: "pyth.png" },
    pyth: { name: "Pyth", iconName: "pyth.png" },
    chainlink: { name: "Chainlink", iconName: "chainlink.png" },
    redstone: { name: "RedStone", iconName: "redstone.jpg" },
    switchboard: { name: "Switchboard", iconName: "switchboard.png" },
    stake_pool: { name: "Stake Pool", iconName: "stake.png" },
    meteora_lp: { name: "Meteora LP", iconName: "met-lp.png" },
    meteora_vault: { name: "Meteora Vault", iconName: "met-vault.png" },
    orca_whirlpool: { name: "Orca Whirlpool", iconName: "orca.png" },
    raydium_clmm: { name: "Raydium", iconName: "raydium.png" },
    flp: { name: "FLP", iconName: "flash.png" },
    loopscale_vault_lp: { name: "Loopscale Vault LP", iconName: "loopscale.png" },
    loopscale_nav: { name: "Loopscale NAV", iconName: "loopscale.png" },
    exponent_token: { name: "Exponent Token", iconName: "exponent.png" },
    exponent_lp_token: { name: "Exponent LP Token", iconName: "exponent.png" },
    exponent_discount_to_maturity: { name: "Exponent", iconName: "exponent.png" },
    exponent_tranche: { name: "Exponent Tranche", iconName: "exponent.png" },
    jupiter_lend: { name: "Jupiter Lend", iconName: "jup-lend.png" },
    stabble_lp_token: { name: "Stabble LP Token", iconName: "stabble.png" },
    perena_lp_token: { name: "Perena LP Token", iconName: "perena.png" },
    ratex_pt_token: { name: "RateX LP Token", iconName: "ratex.png" },
    adrena_lp_token: { name: "Adrena LP Token", iconName: "adrena.png" },
    ether_fuse_stable_bond: { name: "EtherFuse Stable Bond", iconName: "etherfuse.jpg" },
    fx: { name: "Fx", iconName: "pyth.png" }
};

// Tolerates providers the backend knows about before this build does.
export function getOracleMeta(oracleProvider: OracleProvider | undefined) {
    const meta = ORACLE_META[oracleProvider as Exclude<OracleProvider, "unknown">];
    if (!meta) return undefined;
    return { image: `${S3_PATH}/oracles/${meta.iconName}`, name: meta.name };
}

type OracleDisplayProps = {
    oracleAccount?: string;
    oracleProvider?: OracleProvider;
    usdPrice?: number;
    variant?: TextVariant;

    showName?: boolean;
    showImage?: boolean;
    showPrice?: boolean;
    showAddress?: boolean;

    spacing?: number;
};

export function OracleTagAddress(props: {
    oracleAccount: string;
    oracleProvider: OracleProvider;
    variant: TextVariant;
}) {
    return <OracleDisplay {...props} showName showAddress />;
}
export function OracleDisplay({
    oracleAccount,
    oracleProvider,
    usdPrice,
    variant = "body2",
    showName = false,
    showImage = false,
    showPrice = false,
    showAddress = true,
    spacing = 0.5
}: OracleDisplayProps) {
    if (!oracleProvider) return null;

    const meta = getOracleMeta(oracleProvider);

    return (
        <Row spacing={spacing}>
            {showPrice && usdPrice !== undefined && <Text variant={variant}>{formatUsd(usdPrice)}</Text>}

            {showImage && (
                <Image size={`${FONT_SIZES[variant]}px`} variant="circle" src={meta?.image} loading={!meta} />
            )}

            {showName && meta && (
                <Text sx={{ whiteSpace: "nowrap" }} variant={variant}>
                    {meta.name}
                </Text>
            )}

            {showAddress && oracleAccount && <AddressText variant={variant} showText={false} address={oracleAccount} />}
        </Row>
    );
}
export type OracleDetailProps = {
    oracleProvider: OracleProvider;
    oracleAccount: string;
    usdPrice?: number;
    tokenSymbol: string;
};

function OracleStatTooltip({ oracles }: { oracles: OracleDetailProps[] }) {
    return (
        <Column spacing={1} py={1}>
            <Column px={1}>
                <Text variant="body2" fontWeight="bold">
                    Oracles
                </Text>
            </Column>
            <Divider />
            <Column px={1} spacing={0.5}>
                {oracles.map(({ oracleProvider, oracleAccount, usdPrice, tokenSymbol }, i) => {
                    const { name, image } = getOracleMeta(oracleProvider) || {};
                    return (
                        <Row spaceBetween key={`${oracleAccount}-${i}-${oracleProvider}`} spacing={2}>
                            <Row spacing={0.5}>
                                <Text variant="body2">{tokenSymbol}</Text>
                                {usdPrice && (
                                    <Text variant="body2" color="caption">
                                        {formatUsd(usdPrice)}
                                    </Text>
                                )}
                            </Row>
                            <Row spacing={0.5}>
                                {oracleAccount && (
                                    <AddressText variant="body2" showText showIcon={false} address={oracleAccount} />
                                )}

                                {name && (
                                    <TagText textVariant="body2" sx={{ width: "max-content" }}>
                                        {image && <Image size="14px" variant="circle" src={image} />}
                                        {name}
                                    </TagText>
                                )}
                            </Row>
                        </Row>
                    );
                })}
            </Column>
        </Column>
    );
}

export function OracleIcons({ oracles, variant = "body1" }: { oracles: OracleDetailProps[]; variant?: TextVariant }) {
    return (
        <Tooltip
            minTooltipWidth="360px"
            noPadding
            title={<OracleStatTooltip oracles={oracles} />}
            placement="bottom"
            reverseColors
        >
            <Row spacing={-FONT_SIZES[variant] / 4}>
                {oracles.map(({ oracleProvider, oracleAccount }, i) => {
                    const { image } = getOracleMeta(oracleProvider) || {};
                    return image ? (
                        <Image
                            key={`${oracleAccount}-${i}-${oracleProvider}`}
                            size={`${FONT_SIZES[variant]}px`}
                            variant="circle"
                            src={image}
                        />
                    ) : null;
                })}
            </Row>
        </Tooltip>
    );
}
